import React from 'react';
import { useDeviceType } from '@/hooks/use-mobile';
import { usePWABanner } from '@/hooks/usePWABanner';
import MainLayout from '@/components/layout/MainLayout';
import BottomNavigation from './BottomNavigation';
import PWAInstallBanner from './PWAInstallBanner';
import { cn } from '@/lib/utils';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
}

const MobileLayout: React.FC<ResponsiveLayoutProps> = ({ children }) => {
  const { isBannerVisible } = usePWABanner();
  
  return (
    <div className="min-h-screen bg-background">
      {/* PWA Install Banner */}
      <PWAInstallBanner />
      
      {/* Mobile content with dynamic padding for banner */}
      <div className={cn(
        "flex-1 p-4 pb-20",
        isBannerVisible ? "pt-16" : "pt-4"
      )}>
        <div className="mx-auto max-w-xxl">
          {children}
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({ children }) => {
  const { isMobile } = useDeviceType();

  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Mobile-specific container for full-width content
export const MobileContainer: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'full';
}> = ({ 
  children, 
  className,
  maxWidth = 'md'
}) => {
  const { isMobile } = useDeviceType();
  
  if (!isMobile) {
    return <>{children}</>;
  }

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    full: 'max-w-full'
  };

  return (
    <div className={cn(
      "mx-auto w-full",
      maxWidthClasses[maxWidth],
      className
    )}>
      {children}
    </div>
  );
};

// Mobile-specific spacing component
export const MobileSpacing: React.FC<{ 
  size?: 'sm' | 'md' | 'lg' | 'xl';
}> = ({ size = 'md' }) => {
  const { isMobile } = useDeviceType();
  
  if (!isMobile) return null;

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-4',
    lg: 'h-6',
    xl: 'h-8'
  };

  return <div className={sizeClasses[size]} />;
};

export default ResponsiveLayout;
